import type { AdManagerNamedOption } from '@/redux/slices/adManager/adManager';

import type {
  AdManagerFormState,
  AdManagerPublishValidationError
} from './types';
import { getCampaignPlainTextFromRichText } from '@/lomavisSharedUi/templates/sections/LomavisCampaignBuilder1/richText';
import {
  DSA_REGULATED_COUNTRY_CODES,
  MAX_AGE,
  MIN_AGE,
  REAL_SPECIAL_AD_CATEGORY_IDS,
  RESTRICTED_DETAILED_TARGETING_SPECIAL_AD_CATEGORY_IDS,
  SPECIAL_AD_CATEGORY_MIN_RADIUS_KM
} from './constants';

export const isConversionTrackingObjective = (objective: string) =>
  ['OUTCOME_LEADS', 'OUTCOME_SALES'].includes(objective);

export const isPixelTrackingObjective = (objective: string) =>
  isConversionTrackingObjective(objective);

const supportedTargetEventIdsByObjective: Record<string, Set<string>> = {
  OUTCOME_LEADS: new Set(['COMPLETE_REGISTRATION', 'CONTACT', 'LEAD']),
  OUTCOME_SALES: new Set([
    'ADD_TO_CART',
    'COMPLETE_REGISTRATION',
    'CONTENT_VIEW',
    'INITIATED_CHECKOUT',
    'PURCHASE'
  ])
};

const hasNamedOption = (option: AdManagerNamedOption | null) =>
  Boolean(option?.id && option?.name);

const validationKeys = {
  campaignNameRequired: 'adManager.validation.campaignNameRequired',
  dailyBudgetMinimumDuration: 'adManager.validation.dailyBudgetMinimumDuration',
  dsaBeneficiaryRequired: 'adManager.validation.dsaBeneficiaryRequired',
  dsaPayorRequired: 'adManager.validation.dsaPayorRequired',
  endDateAfterComputedStart: 'adManager.validation.endDateAfterComputedStart',
  endDateBeforeStartDate: 'adManager.validation.endDateBeforeStartDate',
  endDateRequired: 'adManager.validation.endDateRequired',
  ageMaxOutOfRange: 'adManager.validation.ageMaxOutOfRange',
  ageMinOutOfRange: 'adManager.validation.ageMinOutOfRange',
  ageRangeInvalid: 'adManager.validation.ageRangeInvalid',
  landingUrlRequired: 'adManager.validation.landingUrlRequired',
  maxBidInvalid: 'adManager.validation.maxBidInvalid',
  maxBidRequired: 'adManager.validation.maxBidRequired',
  mediaRequired: 'adManager.validation.mediaRequired',
  pixelRequired: 'adManager.validation.pixelRequired',
  placementsRequired: 'adManager.validation.placementsRequired',
  primaryTextRequired: 'adManager.validation.primaryTextRequired',
  specialAdAgeMax: 'adManager.validation.specialAdAgeMax',
  specialAdAgeMin: 'adManager.validation.specialAdAgeMin',
  specialAdCountryMismatch: 'adManager.validation.specialAdCountryMismatch',
  specialAdCountryRequired: 'adManager.validation.specialAdCountryRequired',
  specialAdDetailedTargetingUnsupported:
    'adManager.validation.specialAdDetailedTargetingUnsupported',
  specialAdGenderAll: 'adManager.validation.specialAdGenderAll',
  specialAdLocationRadiusRequired:
    'adManager.validation.specialAdLocationRadiusRequired',
  specialAdRadiusRequired: 'adManager.validation.specialAdRadiusRequired',
  startDateRequired: 'adManager.validation.startDateRequired',
  targetEventRequired: 'adManager.validation.targetEventRequired',
  targetEventUnavailableForObjective:
    'adManager.validation.targetEventUnavailableForObjective',
  targetLocationRequired: 'adManager.validation.targetLocationRequired'
} as const;

const getDateAtLocalMidnight = (dateValue: string) => {
  if (!dateValue) return null;

  const [year, month, day] = dateValue.split('-').map(Number);
  if (!year || !month || !day) return null;

  return new Date(year, month - 1, day);
};

const getComputedMetaStartDate = (startDate: string) => {
  const start = getDateAtLocalMidnight(startDate);
  if (!start) return null;

  const now = new Date();
  if (
    start.getFullYear() === now.getFullYear() &&
    start.getMonth() === now.getMonth() &&
    start.getDate() === now.getDate() &&
    start <= now
  ) {
    return new Date(now.getTime() + 10 * 60 * 1000);
  }

  return start;
};

const toMinorUnitAmount = (amount: string) => {
  const rawAmount = amount.trim();
  if (!rawAmount) return null;

  const parsedAmount = Number(rawAmount);
  if (!Number.isFinite(parsedAmount)) return null;

  return Math.round(parsedAmount * 100);
};

export const getRealSpecialAdCategories = (specialAdCategories: string[]) =>
  specialAdCategories.filter((category) =>
    REAL_SPECIAL_AD_CATEGORY_IDS.has(category)
  );

export const hasRestrictedSpecialAdCategory = (specialAdCategories: string[]) =>
  specialAdCategories.some((category) =>
    RESTRICTED_DETAILED_TARGETING_SPECIAL_AD_CATEGORY_IDS.has(category)
  );

export const getSpecialAdCategoryCountryCodes = (
  form: Pick<AdManagerFormState, 'locations' | 'targetCountries'>
) => {
  const countries: string[] = [];

  const addFromEntry = (entry: any) => {
    const rawData = (entry as any).raw_data || {};
    const targetType = String(rawData.type || '').toLowerCase();
    const targetId = String(
      rawData.key || rawData.id || entry.id || ''
    ).trim();
    let countryCode = String(rawData.country_code || '')
      .trim()
      .toUpperCase();

    if (targetType === 'country' || /^[a-z]{2}$/i.test(targetId)) {
      countryCode = (countryCode || targetId).toUpperCase();
    }

    if (countryCode && !countries.includes(countryCode)) {
      countries.push(countryCode);
    }
  };

  form.locations.forEach(addFromEntry);
  (form.targetCountries || []).forEach(addFromEntry);

  return countries;
};

const getNormalizedCountryCodes = (countryCodes: string[]) =>
  countryCodes
    .map((countryCode) =>
      String(countryCode || '')
        .trim()
        .toUpperCase()
    )
    .filter(Boolean);

export const deriveSpecialAdCategoryCountry = (
  form: Pick<AdManagerFormState, 'locations' | 'targetCountries'>,
  adAccountCountry?: string
): string[] => {
  const fromTargeting = getSpecialAdCategoryCountryCodes(form);
  if (fromTargeting.length > 0) {
    return Array.from(new Set(fromTargeting)).sort();
  }

  const fallback = String(adAccountCountry || '').trim().toUpperCase();
  return /^[A-Z]{2}$/.test(fallback) ? [fallback] : [];
};


const targetsDsaRegulatedCountry = (
  form: Pick<AdManagerFormState, 'locations' | 'targetCountries'>
) =>
  getSpecialAdCategoryCountryCodes(form).some((countryCode) =>
    DSA_REGULATED_COUNTRY_CODES.has(countryCode)
  );

export const validateAdManagerPublishForm = (
  form: AdManagerFormState
): AdManagerPublishValidationError[] => {
  const errors: AdManagerPublishValidationError[] = [];
  const addError = (
    field: string,
    messageKey: string,
    tabKey: AdManagerPublishValidationError['tabKey']
  ) => {
    errors.push({ field, message: messageKey, messageKey, tabKey });
  };

  if (!form.campaignName.trim()) {
    addError('name', validationKeys.campaignNameRequired, 'campaign');
  }

  if (!form.placements.some((placement) => placement.selected)) {
    addError('placements', validationKeys.placementsRequired, 'campaign');
  }

  if (
    form.locations.length === 0 &&
    (form.targetCountries || []).length === 0
  ) {
    addError(
      'target_locations',
      validationKeys.targetLocationRequired,
      'targeting'
    );
  }

  if (targetsDsaRegulatedCountry(form)) {
    if (!form.dsaBeneficiary.trim()) {
      addError(
        'dsa_beneficiary',
        validationKeys.dsaBeneficiaryRequired,
        'budget'
      );
    }
    if (!form.dsaPayor.trim()) {
      addError('dsa_payor', validationKeys.dsaPayorRequired, 'budget');
    }
  }

  if (form.ageRange[0] < MIN_AGE || form.ageRange[0] > MAX_AGE) {
    addError('age_min', validationKeys.ageMinOutOfRange, 'targeting');
  }

  if (form.ageRange[1] < MIN_AGE || form.ageRange[1] > MAX_AGE) {
    addError('age_max', validationKeys.ageMaxOutOfRange, 'targeting');
  }

  if (form.ageRange[0] > form.ageRange[1]) {
    addError('age_max', validationKeys.ageRangeInvalid, 'targeting');
  }

  const realSpecialAdCategories = getRealSpecialAdCategories(
    form.specialAdCategories
  );
  if (realSpecialAdCategories.length > 0) {
    const selectedSpecialAdCountryCodes = getNormalizedCountryCodes(
      form.specialAdCategoryCountry
    );

    if (selectedSpecialAdCountryCodes.length === 0) {
      addError(
        'special_ad_category_country',
        validationKeys.specialAdCountryRequired,
        'targeting'
      );
    }
  }

  if (hasRestrictedSpecialAdCategory(form.specialAdCategories)) {
    if (form.ageRange[0] !== 18) {
      addError('age_min', validationKeys.specialAdAgeMin, 'targeting');
    }

    if (form.ageRange[1] !== 65) {
      addError('age_max', validationKeys.specialAdAgeMax, 'targeting');
    }

    if (form.gender !== 'all') {
      addError('gender', validationKeys.specialAdGenderAll, 'targeting');
    }

    if (form.interests.length > 0) {
      addError(
        'target_interests',
        validationKeys.specialAdDetailedTargetingUnsupported,
        'targeting'
      );
    }

    if (form.behaviors.length > 0) {
      addError(
        'target_behaviors',
        validationKeys.specialAdDetailedTargetingUnsupported,
        'targeting'
      );
    }

    const hasInvalidCampaignRadius =
      Number(form.radiusKm) < SPECIAL_AD_CATEGORY_MIN_RADIUS_KM;
    const hasInvalidLocationRadius = form.locations.some((location) => {
      const radiusKm = Number(location.radiusKm ?? form.radiusKm);
      return Number.isFinite(radiusKm)
        ? radiusKm < SPECIAL_AD_CATEGORY_MIN_RADIUS_KM
        : true;
    });

    if (hasInvalidCampaignRadius) {
      addError(
        'radius_km',
        validationKeys.specialAdRadiusRequired,
        'targeting'
      );
    }

    if (hasInvalidLocationRadius) {
      addError(
        'target_locations',
        validationKeys.specialAdLocationRadiusRequired,
        'targeting'
      );
    }
  }

  if (form.selectedMediaFiles.length === 0) {
    addError('media_image_uuids', validationKeys.mediaRequired, 'creative');
  }

  if (!getCampaignPlainTextFromRichText(form.ad.primaryText).trim()) {
    addError('primary_text', validationKeys.primaryTextRequired, 'creative');
  }

  if (!form.ad.landingUrl.trim()) {
    addError('landing_url', validationKeys.landingUrlRequired, 'creative');
  }

  if (!form.startDate) {
    addError('start_date', validationKeys.startDateRequired, 'budget');
  }

  if (!form.endDate) {
    addError('end_date', validationKeys.endDateRequired, 'budget');
  }

  if (form.startDate && form.endDate) {
    const rawStartDate = getDateAtLocalMidnight(form.startDate);
    const rawEndDate = getDateAtLocalMidnight(form.endDate);

    if (rawStartDate && rawEndDate && rawEndDate < rawStartDate) {
      addError(
        'end_date',
        validationKeys.endDateBeforeStartDate,
        'budget'
      );
    } else {
      const computedStartDate = getComputedMetaStartDate(form.startDate);
      const endDate = rawEndDate;

      if (computedStartDate && endDate) {
        if (endDate <= computedStartDate) {
          addError(
            'end_date',
            validationKeys.endDateAfterComputedStart,
            'budget'
          );
        } else if (
          form.budgetType === 'daily' &&
          endDate.getTime() - computedStartDate.getTime() <
            24 * 60 * 60 * 1000
        ) {
          addError(
            'end_date',
            validationKeys.dailyBudgetMinimumDuration,
            'budget'
          );
        }
      }
    }
  }

  if (form.bidStrategy === 'manual') {
    const maxBidAmount = toMinorUnitAmount(form.maxBidAmount);
    if (!form.maxBidAmount.trim()) {
      addError('max_bid_amount_cents', validationKeys.maxBidRequired, 'budget');
    } else if (maxBidAmount === null || maxBidAmount < 1) {
      addError('max_bid_amount_cents', validationKeys.maxBidInvalid, 'budget');
    }
  }

  if (isConversionTrackingObjective(form.objective)) {
    if (!hasNamedOption(form.pixel)) {
      addError('pixel', validationKeys.pixelRequired, 'targeting');
    }
    if (!hasNamedOption(form.targetEvent)) {
      addError('target_event', validationKeys.targetEventRequired, 'targeting');
    } else if (
      !supportedTargetEventIdsByObjective[form.objective]?.has(
        String(form.targetEvent?.id || '')
      )
    ) {
      addError(
        'target_event',
        validationKeys.targetEventUnavailableForObjective,
        'targeting'
      );
    }
  }

  return errors;
};
